/**
 * Display fallbacks for identifiers the ontology left without a human label. Every caller tries
 * declared metadata first; these only turn machine shapes (`snake_case`, `dotted.names`,
 * `CamelCase`) into readable words. They never invent business vocabulary.
 */

/** Short all-consonant tokens read as acronyms (`crm` → `CRM`, `sku` → `SKU`). */
function word(token: string): string {
  if (!token) return token
  if (token.length <= 4 && !/[aeiouy]/i.test(token)) return token.toUpperCase()
  if (/^[A-Z0-9]+$/.test(token) && token.length <= 4) return token
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase()
}

/** Split `CashCollectionAccount`, `cash_collection_account`, `CRMChannelInbox` into words. */
export function words(id: string): string[] {
  return id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/[\s_.\-/]+/)
    .filter(Boolean)
}

/** `name_en` → `Name En`, `CRMChannelInbox` → `CRM Channel Inbox`. */
export function titleCase(id: string): string {
  return words(id).map(word).join(" ")
}

/** Sentence case for field labels: `price_minor` → `Price minor`, keeping acronyms. */
export function sentenceCase(id: string): string {
  const parts = words(id)
  return parts
    .map((token, i) => {
      const titled = word(token)
      if (titled === titled.toUpperCase() && titled.length > 1) return titled
      return i === 0 ? titled : titled.toLowerCase()
    })
    .join(" ")
}

/** The local part of a qualified type name: `showcase.Order` → `Order`. */
export function localName(qualified: string): string {
  const i = qualified.lastIndexOf(".")
  return i === -1 ? qualified : qualified.slice(i + 1)
}

/** English plural of a label's last word (`Delivery` → `Deliveries`, `Address` → `Addresses`). */
export function plural(label: string): string {
  const m = /^(.*?)(\S+)$/.exec(label)
  if (!m) return label
  const [, head, last] = m as unknown as [string, string, string]
  const lower = last.toLowerCase()
  if (/(data|settings|media|series|news|info|ings|ics)$/.test(lower)) return label
  if (/[^aeiou]y$/.test(lower)) return `${head}${last.slice(0, -1)}ies`
  if (/(s|x|z|ch|sh)$/.test(lower)) return `${head}${last}es`
  return `${head}${last}s`
}

/** Strip the namespace prefix shared by several tenant namespaces (`acme_crm`, `acme_cash` → `crm`, `cash`). */
export function sharedPrefix(namespaces: readonly string[]): string {
  const candidates = namespaces.filter((ns) => ns.includes("_"))
  if (candidates.length < 2) return ""
  const counts = new Map<string, number>()
  for (const ns of candidates) {
    const prefix = `${ns.split("_")[0]}_`
    counts.set(prefix, (counts.get(prefix) ?? 0) + 1)
  }
  const [best] = [...counts.entries()].sort((a, b) => b[1] - a[1])
  return best && best[1] >= 2 ? best[0] : ""
}
